import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Badge } from "../components/ui/badge";
import { LandingNav } from "../components/LandingNav";
import { Logo } from "../components/Logo";
import { SampleCallPlayer } from "../components/SampleCallPlayer";
import { track, EVENTS } from "../lib/analytics";
import { KinvantaCard } from "../components/KinvantaCard";
import { TestCallCard } from "../components/TestCallCard";
import {
  Phone,
  Heart,
  Sparkles,
  CheckCircle2,
  Shield,
  BellRing,
  Smile,
  ArrowRight,
  Star,
  MessageCircle,
  Users,
} from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1758691031979-6128d4029604?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwyfHxzZW5pb3IlMjB0YWxraW5nJTIwb24lMjBwaG9uZSUyMGhvbWV8ZW58MHx8fHwxNzc2NTQ5NTU5fDA&ixlib=rb-4.1.0&q=85";
const BENEFIT1 =
  "https://images.unsplash.com/photo-1758273706077-cd89007983e8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxhZHVsdCUyMHRleHRpbmclMjBzbWlsaW5nfGVufDB8fHx8MTc3NjUzOTc5OHww&ixlib=rb-4.1.0&q=85";
const BENEFIT2 =
  "https://images.unsplash.com/photo-1544097797-5eceea65516f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHw0fHxtdWx0aWdlbmVyYXRpb25hbCUyMGZhbWlseSUyMGh1Z2dpbmd8ZW58MHx8fHwxNzc2NTM5Nzk3fDA&ixlib=rb-4.1.0&q=85";

const plans = [
  {
    id: "starter",
    name: "Starter",
    price: 29,
    tagline: "Gentle check-ins to stay close",
    features: ["3 check-in calls weekly", "Email summaries", "Mood tracking", "1 loved one"],
  },
  {
    id: "plus",
    name: "Plus",
    price: 49,
    popular: true,
    tagline: "Our most loved plan — peace of mind every day",
    features: ["Daily check-in calls", "Email summaries", "SMS alerts", "Better memory continuity", "1 loved one"],
  },
  {
    id: "family",
    name: "Family",
    price: 79,
    tagline: "For larger families caring together",
    features: ["Everything in Plus", "Multiple recipients", "Priority support", "Enhanced alerts"],
  },
];

const faqs = [
  { q: "Do my parents need to download an app?", a: "No. CherishLink calls a regular phone — landline or mobile. There is nothing for them to install or learn." },
  { q: "What does the call sound like?", a: "A warm, natural voice calls at the scheduled time and has a friendly conversation about family, memories, and today's plans. Short sentences, patient pacing, and never clinical." },
  { q: "Is this a medical or caregiver service?", a: "No. CherishLink is companionship and peace of mind. It does not replace caregivers, doctors, or medical monitoring." },
  { q: "Can I cancel anytime?", a: "Yes. You can pause, upgrade, downgrade, or cancel from your dashboard at any time. No phone trees, no friction." },
  { q: "How do I know if something is wrong?", a: "If CherishLink hears signs of distress, or if calls are repeatedly missed, we send you a gentle SMS or email alert so you can reach out personally." },
];

export default function Landing() {
  return (
    <div className="min-h-screen cherishlink-gradient-bg">
      <LandingNav />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-10 md:pt-20 pb-16 md:pb-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="fade-in-up">
            <Badge className="bg-accent text-accent-foreground hover:bg-accent rounded-full px-4 py-1.5 font-medium mb-6" data-testid="hero-badge">
              <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Warm AI companionship, built for families
            </Badge>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-foreground">
              Meaningful conversations<br className="hidden sm:inline" /> for older adults.
              <span className="text-primary"> Peace of mind for families.</span>
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-xl leading-relaxed">
              AI-powered friendly phone companionship with family updates and wellness insights.
              No app required for them — just a phone.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Button asChild size="lg" className="rounded-full h-12 px-8 text-base" data-testid="hero-cta-trial">
                <Link to="/signup" onClick={() => track(EVENTS.LANDING_CTA_CLICKED, { location: "hero" })}>
                  Start Free Trial <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full h-12 px-8 text-base border-2" data-testid="hero-cta-how">
                <a href="#how-it-works">See How It Works</a>
              </Button>
            </div>
            <div className="mt-6">
              <SampleCallPlayer />
            </div>
            <div className="mt-6">
              <TestCallCard variant="inline" source="landing_hero" />
            </div>
            {/* Mobile-only Kinvanta above-fold (hidden md+) */}
            <div className="mt-6 md:hidden">
              <KinvantaCard variant="sidecard" source="landing_mobile_above_fold" />
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-muted-foreground">
              <TrustBadge icon={Phone} label="No app needed for seniors" />
              <TrustBadge icon={Shield} label="Phone-based" />
              <TrustBadge icon={CheckCircle2} label="Cancel anytime" />
            </div>
          </div>
          <div className="relative fade-in-up animate-delay-200">
            <div className="relative rounded-3xl overflow-hidden soft-shadow">
              <img src={HERO_IMG} alt="Older woman smiling warmly on a phone call at home" className="w-full h-[520px] object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-5 soft-shadow max-w-xs hidden md:block">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-secondary/15 grid place-items-center">
                  <Smile className="h-5 w-5 text-secondary" />
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Mom today</div>
                  <div className="text-sm font-semibold">Feeling upbeat · 8/10</div>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
                "Talked about the roses blooming and asked about the grandkids."
              </p>
            </div>
            <div className="absolute -top-6 -right-6 bg-white rounded-2xl p-4 soft-shadow hidden md:flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-primary/10 grid place-items-center">
                <BellRing className="h-4 w-4 text-primary" />
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Next call</div>
                <div className="text-sm font-semibold">Tomorrow · 10:30 AM</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Kinvanta above-fold on MOBILE only — rendered inside hero above */}

      {/* How it works */}
      <section id="how-it-works" className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-28">
        <div className="max-w-2xl">
          <div className="text-sm font-semibold text-secondary uppercase tracking-wider">How it works</div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            Three gentle steps to stay close.
          </h2>
        </div>
        <div className="mt-16 grid md:grid-cols-3 gap-8">
          {[
            { n: 1, icon: Phone, title: "Add their phone number", body: "Tell us who to call and the best times. No app, no setup on their end." },
            { n: 2, icon: MessageCircle, title: "CherishLink makes friendly calls", body: "Warm, patient conversations about family, memories, and today's plans." },
            { n: 3, icon: BellRing, title: "You get peaceful updates", body: "Short summaries, mood trends, and gentle alerts when something feels off." },
          ].map((s, i) => (
            <Card key={s.n} className={`rounded-3xl border-border/60 soft-shadow hover-lift fade-in-up animate-delay-${(i + 1) * 100}`}>
              <CardContent className="p-8">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-2xl bg-accent grid place-items-center">
                    <s.icon className="h-5 w-5 text-primary" />
                  </div>
                  <div className="text-sm font-semibold text-muted-foreground">Step {s.n}</div>
                </div>
                <h3 className="mt-6 text-xl font-semibold">{s.title}</h3>
                <p className="mt-3 text-muted-foreground leading-relaxed">{s.body}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Benefits */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-28">
        <BenefitRow
          image={BENEFIT1}
          eyebrow="Less worry"
          title="Reduce the guilt of living far away."
          body="When you can't call every day, CherishLink can. Warm conversations your parent looks forward to, and short summaries that let you breathe easier."
          bullets={["Calm in-between-visits updates", "Mood trend over time", "Gentle alerts only when needed"]}
        />
        <BenefitRow
          reverse
          image={BENEFIT2}
          eyebrow="More connection"
          title="Help combat the loneliness of aging."
          body="Our friendly companion remembers past conversations, asks about family by name, and treats your loved one with warmth and dignity — every single call."
          bullets={["Remembers names & stories", "Patient, never robotic", "Tailored conversation topics"]}
        />
      </section>

      {/* Social proof */}
      <section className="bg-white border-y border-border/60">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24">
          <div className="max-w-2xl">
            <div className="text-sm font-semibold text-secondary uppercase tracking-wider">Families love CherishLink</div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight">
              Real peace of mind, in their own words.
            </h2>
          </div>
          <div className="mt-14 grid md:grid-cols-2 gap-6">
            {[
              { q: "Mom looks forward to every call. It has brought back her sparkle. I cry reading the summaries.", a: "Rebecca M.", role: "Daughter · Seattle" },
              { q: "CherishLink helps me feel less guilty living far away. I feel close again — even on the busiest weeks.", a: "James K.", role: "Son · Austin" },
            ].map((t, i) => (
              <Card key={i} className="bg-accent/40 border-accent rounded-3xl soft-shadow">
                <CardContent className="p-10">
                  <div className="flex gap-1 mb-4">
                    {Array.from({ length: 5 }).map((_, k) => (
                      <Star key={k} className="h-4 w-4 fill-primary text-primary" />
                    ))}
                  </div>
                  <blockquote className="font-display text-xl md:text-2xl text-foreground leading-relaxed">
                    "{t.q}"
                  </blockquote>
                  <div className="mt-6 flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-primary/15 grid place-items-center text-primary font-semibold">
                      {t.a[0]}
                    </div>
                    <div>
                      <div className="text-sm font-semibold">{t.a}</div>
                      <div className="text-xs text-muted-foreground">{t.role}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section
        id="pricing"
        className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-28"
        ref={(el) => {
          if (!el || el._tracked) return;
          const obs = new IntersectionObserver((entries) => {
            entries.forEach((e) => {
              if (e.isIntersecting && !el._tracked) {
                el._tracked = true;
                track(EVENTS.PRICING_VIEWED);
                obs.disconnect();
              }
            });
          }, { threshold: 0.3 });
          obs.observe(el);
        }}
      >
        <div className="max-w-2xl">
          <div className="text-sm font-semibold text-secondary uppercase tracking-wider">Pricing</div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            Simple plans. Warm results.
          </h2>
          <p className="mt-4 text-muted-foreground text-lg">
            Start with a 7-day free trial. Upgrade, downgrade, or cancel anytime.
          </p>
        </div>
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {plans.map((p) => (
            <Card
              key={p.id}
              data-testid={`pricing-${p.id}`}
              className={`rounded-3xl soft-shadow hover-lift ${
                p.popular ? "border-secondary border-2" : "border-border/60"
              }`}
            >
              <CardContent className="p-8">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold">{p.name}</h3>
                  {p.popular && (
                    <Badge className="bg-secondary text-secondary-foreground hover:bg-secondary rounded-full">Most Popular</Badge>
                  )}
                </div>
                <div className="mt-6 flex items-end gap-1">
                  <div className="text-5xl font-display font-bold tracking-tight">${p.price}</div>
                  <div className="text-muted-foreground mb-2">/month</div>
                </div>
                <p className="mt-2 text-sm text-muted-foreground">{p.tagline}</p>
                <ul className="mt-6 space-y-3">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-secondary mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Button asChild className={`mt-8 w-full rounded-full h-11 ${p.popular ? "" : "bg-foreground hover:bg-foreground/90"}`} data-testid={`pricing-cta-${p.id}`}>
                  <Link to="/signup" onClick={() => track(EVENTS.LANDING_CTA_CLICKED, { location: "pricing", plan: p.id })}>Start free trial</Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-6 md:px-10 py-24 md:py-28">
        <div className="text-sm font-semibold text-secondary uppercase tracking-wider">Frequently asked</div>
        <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight">Questions families ask.</h2>
        <Accordion type="single" collapsible className="mt-10">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`f-${i}`} className="border-border/60">
              <AccordionTrigger className="text-left text-base font-semibold" data-testid={`faq-${i}`}>
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Final CTA */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 pb-16">
        <div className="rounded-3xl bg-foreground text-background p-12 md:p-16 soft-shadow text-center">
          <Heart className="h-8 w-8 mx-auto text-secondary" />
          <h2 className="mt-6 font-display text-3xl md:text-5xl font-bold tracking-tight">
            Give yourself peace of mind.
          </h2>
          <p className="mt-4 text-white/70 max-w-xl mx-auto">
            Try CherishLink free for 7 days. Your parent will look forward to every call.
          </p>
          <Button asChild size="lg" className="mt-8 rounded-full h-12 px-8 bg-white text-foreground hover:bg-white/90" data-testid="footer-cta-trial">
            <Link to="/signup" onClick={() => track(EVENTS.LANDING_CTA_CLICKED, { location: "footer" })}>Start Free Trial <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
        </div>
      </section>

      {/* Kinvanta sister-product early-access strip */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 pb-20">
        <KinvantaCard variant="footer" source="landing" />
      </section>

      {/* Footer */}
      <footer className="border-t border-border/60 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <Logo size="sm" />
          <div className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} CherishLink. Built with care for families.
          </div>
          <div className="flex items-center gap-6 text-sm text-muted-foreground">
            <a href="#faq" className="hover:text-foreground">FAQ</a>
            <a href="#pricing" className="hover:text-foreground">Pricing</a>
            <Link to="/login" className="hover:text-foreground">Login</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function TrustBadge({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4 text-secondary" />
      <span>{label}</span>
    </div>
  );
}

function BenefitRow({ image, eyebrow, title, body, bullets, reverse }) {
  return (
    <div className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center py-10 ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}>
      <div className="rounded-3xl overflow-hidden soft-shadow">
        <img src={image} alt={title} className="w-full h-[420px] object-cover" />
      </div>
      <div>
        <div className="text-sm font-semibold text-secondary uppercase tracking-wider">{eyebrow}</div>
        <h3 className="mt-3 font-display text-3xl md:text-4xl font-bold tracking-tight">{title}</h3>
        <p className="mt-4 text-muted-foreground text-lg leading-relaxed">{body}</p>
        <ul className="mt-6 space-y-3">
          {bullets.map((b) => (
            <li key={b} className="flex items-start gap-2">
              <CheckCircle2 className="h-5 w-5 text-secondary mt-0.5 shrink-0" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
